/**
 * Intake Profile Types
 *
 * Type definitions for the adaptive onboarding intake interview.
 * The intake interview asks 7 conversational questions to build a user profile
 * that determines which financial fields are relevant to the user's situation.
 *
 * Structure: users/{userId} (intakeProfile sub-document)
 *
 * ## Usage
 *
 * ```typescript
 * import type { IntakeProfile, LifeStage, KnowledgeLevel } from '../types/intake';
 *
 * const profile: IntakeProfile = {
 *   intakeVersion: 1,
 *   completedAt: new Date(),
 *   lifeStage: 'building',
 *   household: 'couple',
 *   housing: 'own-mortgage',
 *   employment: 'employed-pension',
 *   investmentLevel: 'intermediate',
 *   debtSituation: ['mortgage', 'consumer'],
 *   nearTermEvents: ['family'],
 *   knowledgeLevel: 'standard',
 * };
 * ```
 */

// ── Life Stage ────────────────────────────────────────────────────────

/** Life stage selections for Q1 of the intake interview */
export const LIFE_STAGES = [
  "starting-out",
  "building",
  "peak-earning",
  "approaching",
  "retired",
] as const;

export type LifeStage = (typeof LIFE_STAGES)[number];

// ── Household ─────────────────────────────────────────────────────────

/** Household composition for Q2 */
export const HOUSEHOLD_TYPES = ["solo", "couple", "family"] as const;

export type HouseholdType = (typeof HOUSEHOLD_TYPES)[number];

// ── Housing ───────────────────────────────────────────────────────────

/** Housing situation for Q3 */
export const HOUSING_SITUATIONS = [
  "own-mortgage",
  "own-free",
  "rent",
  "own-investment",
  "other",
] as const;

export type HousingSituation = (typeof HOUSING_SITUATIONS)[number];

// ── Employment ────────────────────────────────────────────────────────

/** Employment type for Q4 */
export const EMPLOYMENT_TYPES = [
  "employed-salary",
  "employed-pension",
  "self-employed",
  "multiple",
  "retired",
] as const;

export type EmploymentType = (typeof EMPLOYMENT_TYPES)[number];

// ── Investment Level ──────────────────────────────────────────────────

/** Investment sophistication for Q5 */
export const INVESTMENT_LEVELS = [
  "beginner",
  "intermediate",
  "diversified",
  "complex",
] as const;

export type InvestmentLevel = (typeof INVESTMENT_LEVELS)[number];

// ── Debt Situation ────────────────────────────────────────────────────

/** Debt types for Q6 (multi-select) */
export const DEBT_SITUATIONS = [
  "debt-free",
  "mortgage",
  "consumer",
  "student",
] as const;

export type DebtSituation = (typeof DEBT_SITUATIONS)[number];

// ── Near-Term Events ──────────────────────────────────────────────────

/** Near-term life events for Q6 multi-select addendum */
export const NEAR_TERM_EVENTS = [
  "buying-home",
  "family",
  "payoff-loans",
  "retiring",
  "career-change",
  "major-purchase",
  "none",
] as const;

export type NearTermEvent = (typeof NEAR_TERM_EVENTS)[number];

// ── Knowledge Level ───────────────────────────────────────────────────

/** Financial knowledge level for Q7 */
export const KNOWLEDGE_LEVELS = ["simple", "standard", "expert"] as const;

export type KnowledgeLevel = (typeof KNOWLEDGE_LEVELS)[number];

// ── UI Mode ───────────────────────────────────────────────────────────

/** UI complexity mode derived from knowledge level */
export const UI_MODES = ["simple", "standard", "expert"] as const;

export type UIMode = (typeof UI_MODES)[number];

// ── Intake Profile ────────────────────────────────────────────────────

/**
 * Complete intake profile stored in Firestore.
 * Captures all intake interview answers and derived state.
 */
export interface IntakeProfile {
  /** Intake interview version (for future migration) */
  intakeVersion: 1;

  /** Timestamp of interview completion */
  completedAt: Date;

  /** Q1: Life stage selection */
  lifeStage: LifeStage;

  /** Q2: Household composition */
  household: HouseholdType;

  /** Q3: Housing situation */
  housing: HousingSituation;

  /** Q4: Employment type */
  employment: EmploymentType;

  /** Q5: Investment sophistication */
  investmentLevel: InvestmentLevel;

  /** Q6: Debt situation (multi-select) */
  debtSituation: DebtSituation[];

  /** Near-term life events (multi-select, from Q6 addendum) */
  nearTermEvents: NearTermEvent[];

  /** Q7: Financial knowledge level */
  knowledgeLevel: KnowledgeLevel;

  /** Per-screen "show all" overrides (user toggled "Show all fields") */
  showAllOverrides?: Partial<Record<string, boolean>>;
}

// ── Field Visibility ──────────────────────────────────────────────────

/** Visibility state for a single field */
export interface FieldVisibility {
  /** Whether the field should be rendered */
  visible: boolean;
  /** Human-readable reason for the visibility state */
  reason: string;
  /** Default value used in calculations when hidden */
  defaultValue?: number;
}

/** Map of field names to their visibility state */
export type FieldVisibilityMap = Record<string, FieldVisibility>;

// ── Assumption Defaults ───────────────────────────────────────────────

/** Age-based assumption defaults for the defaults-first approach */
export interface AssumptionDefaults {
  retirementAge: number;
  lifeExpectancy: number;
  expectedReturnRate: number;
  inflationRate: number;
  expensesAtRetirement: number;
  realEstateAppreciationRate: number;
  healthcareInflationBuffer: number;
  cashEquivalentsPct: number;
  fixedIncomePct: number;
  equitiesPct: number;
}

/**
 * Returns age-appropriate assumption defaults based on life stage.
 *
 * These are Canadian-specific defaults shown transparently in the Quick Peek
 * screen. Users can customize them later in Settings → Assumptions.
 */
export function getAssumptionDefaults(
  lifeStage: LifeStage
): AssumptionDefaults {
  switch (lifeStage) {
    case "starting-out":
      return {
        retirementAge: 65,
        lifeExpectancy: 92,
        expectedReturnRate: 0.07,
        inflationRate: 0.02,
        expensesAtRetirement: -0.2,
        realEstateAppreciationRate: 0.03,
        healthcareInflationBuffer: 0.01,
        cashEquivalentsPct: 10,
        fixedIncomePct: 20,
        equitiesPct: 70,
      };
    case "building":
      return {
        retirementAge: 65,
        lifeExpectancy: 90,
        expectedReturnRate: 0.065,
        inflationRate: 0.02,
        expensesAtRetirement: -0.2,
        realEstateAppreciationRate: 0.03,
        healthcareInflationBuffer: 0.01,
        cashEquivalentsPct: 15,
        fixedIncomePct: 30,
        equitiesPct: 55,
      };
    case "peak-earning":
      return {
        retirementAge: 65,
        lifeExpectancy: 90,
        expectedReturnRate: 0.06,
        inflationRate: 0.02,
        expensesAtRetirement: -0.2,
        realEstateAppreciationRate: 0.03,
        healthcareInflationBuffer: 0.01,
        cashEquivalentsPct: 20,
        fixedIncomePct: 40,
        equitiesPct: 40,
      };
    case "approaching":
      return {
        retirementAge: 65,
        lifeExpectancy: 88,
        expectedReturnRate: 0.055,
        inflationRate: 0.02,
        expensesAtRetirement: -0.2,
        realEstateAppreciationRate: 0.03,
        healthcareInflationBuffer: 0.01,
        cashEquivalentsPct: 25,
        fixedIncomePct: 45,
        equitiesPct: 30,
      };
    case "retired":
      return {
        retirementAge: 65,
        lifeExpectancy: 88,
        expectedReturnRate: 0.05,
        inflationRate: 0.02,
        expensesAtRetirement: -0.2,
        realEstateAppreciationRate: 0.03,
        healthcareInflationBuffer: 0.01,
        cashEquivalentsPct: 30,
        fixedIncomePct: 50,
        equitiesPct: 20,
      };
  }
}

// ── Intake Question Metadata ──────────────────────────────────────────

/** Metadata for an individual intake question option */
export interface IntakeQuestionOption<T extends string = string> {
  /** Value stored in the profile */
  value: T;
  /** Display label */
  label: string;
  /** Icon/emoji for the option card */
  icon: string;
  /** Optional description shown below label */
  description?: string;
}

/** Metadata for a single intake interview question */
export interface IntakeQuestion<T extends string = string> {
  /** Question identifier (q1–q7) */
  id: string;
  /** Question text displayed to the user */
  question: string;
  /** Available options */
  options: IntakeQuestionOption<T>[];
  /** Whether multiple selections are allowed */
  multiSelect: boolean;
  /** Field in IntakeProfile this question maps to */
  profileField: keyof IntakeProfile;
  /** Optional help text explaining why this question is asked */
  helpText?: string;
}

/**
 * Complete question definitions for the 7-question intake interview.
 * Presented one-per-screen on mobile, grouped on desktop.
 */
export const INTAKE_QUESTIONS: IntakeQuestion[] = [
  {
    id: "q1",
    question: "Where are you on your journey?",
    profileField: "lifeStage",
    multiSelect: false,
    helpText:
      "This helps us tailor investment assumptions and time horizons to your stage of life.",
    options: [
      {
        value: "starting-out",
        label: "Just starting out",
        icon: "🎓",
        description: "Beginning your career and financial journey",
      },
      {
        value: "building",
        label: "Building my career",
        icon: "📈",
        description: "Growing income and starting to save",
      },
      {
        value: "peak-earning",
        label: "Peak earning years",
        icon: "🏠",
        description: "Maximizing savings and investments",
      },
      {
        value: "approaching",
        label: "Approaching retirement",
        icon: "🎯",
        description: "Fine-tuning your retirement plan",
      },
      {
        value: "retired",
        label: "Already retired",
        icon: "🌅",
        description: "Managing retirement income",
      },
    ],
  },
  {
    id: "q2",
    question: "Who are you planning for?",
    profileField: "household",
    multiSelect: false,
    helpText:
      "Your plan focuses on you (and your partner). Dependents are considered for expenses but are not the subject of the plan.",
    options: [
      { value: "solo", label: "Just me", icon: "👤" },
      { value: "couple", label: "Me and my partner/spouse", icon: "👥" },
      {
        value: "family",
        label: "My household (partner + dependents)",
        icon: "👨‍👩‍👧",
        description:
          "Plan focuses on you and your partner; dependents affect expenses",
      },
    ],
  },
  {
    id: "q3",
    question: "What's your primary housing situation?",
    profileField: "housing",
    multiSelect: false,
    helpText:
      "Housing is often the largest expense in retirement. This helps us estimate your future costs.",
    options: [
      {
        value: "own-mortgage",
        label: "I own my home (with a mortgage)",
        icon: "🏠",
      },
      { value: "own-free", label: "I own my home (mortgage-free)", icon: "🏡" },
      { value: "rent", label: "I rent", icon: "🏢" },
      {
        value: "own-investment",
        label: "I own my home plus investment property",
        icon: "🏘️",
        description: "Primary residence + rental or investment real estate",
      },
      { value: "other", label: "Other (living with family, etc.)", icon: "🤝" },
    ],
  },
  {
    id: "q4",
    question: "What best describes your work situation?",
    profileField: "employment",
    multiSelect: false,
    helpText:
      "Your employment type affects pension eligibility, tax strategies, and contribution room.",
    options: [
      {
        value: "employed-salary",
        label: "Employed (salary/wages)",
        icon: "💼",
      },
      {
        value: "employed-pension",
        label: "Employed with a pension plan",
        icon: "🏢",
      },
      {
        value: "self-employed",
        label: "Self-employed / business owner",
        icon: "💻",
      },
      {
        value: "multiple",
        label: "Multiple income sources",
        icon: "🔀",
        description: "E.g. salary + freelance, rental income, etc.",
      },
      { value: "retired", label: "Retired / not working", icon: "🏖️" },
    ],
  },
  {
    id: "q5",
    question: "How would you describe your savings and investments?",
    profileField: "investmentLevel",
    multiSelect: false,
    helpText:
      "This helps us show the right level of detail for registered and non-registered accounts.",
    options: [
      {
        value: "beginner",
        label: "Just getting started",
        icon: "🌱",
        description: "Mostly cash or savings account",
      },
      {
        value: "intermediate",
        label: "Some registered accounts",
        icon: "📊",
        description: "Started contributing to registered accounts",
      },
      {
        value: "diversified",
        label: "Diversified portfolio",
        icon: "💰",
        description: "Registered + non-registered investments",
      },
      {
        value: "complex",
        label: "Complex situation",
        icon: "🏛️",
        description: "Multiple account types, employer plans",
      },
    ],
  },
  {
    id: "q6",
    question: "How's your debt picture?",
    profileField: "debtSituation",
    multiSelect: true,
    helpText:
      "Debt payments reduce how much you can save. Select all that apply.",
    options: [
      { value: "debt-free", label: "Debt-free (or close to it)", icon: "✅" },
      { value: "mortgage", label: "Mortgage", icon: "🏠" },
      {
        value: "consumer",
        label: "Consumer debt (credit cards, car loan)",
        icon: "💳",
      },
      { value: "student", label: "Student loans", icon: "📚" },
    ],
  },
  {
    id: "q7",
    question: "How comfortable are you with financial planning?",
    profileField: "knowledgeLevel",
    multiSelect: false,
    helpText:
      "We'll adjust the level of detail and terminology based on your comfort level.",
    options: [
      {
        value: "simple",
        label: "I'm new to financial planning — keep it simple",
        icon: "🌱",
      },
      {
        value: "standard",
        label: "I know the basics of financial planning — show me what matters",
        icon: "📖",
      },
      {
        value: "expert",
        label:
          "I'm experienced with financial planning — give me all the details",
        icon: "🎯",
      },
    ],
  },
];

/**
 * Creates a default (empty) IntakeProfile with sensible starting values.
 * Used as the initial state when starting the intake interview.
 */
export function createDefaultIntakeProfile(): IntakeProfile {
  return {
    intakeVersion: 1,
    completedAt: new Date(),
    lifeStage: "building",
    household: "solo",
    housing: "rent",
    employment: "employed-salary",
    investmentLevel: "beginner",
    debtSituation: ["debt-free"],
    nearTermEvents: ["none"],
    knowledgeLevel: "standard",
  };
}

/**
 * Maps knowledge level to UI mode.
 * Currently 1:1 mapping but allows future divergence.
 */
export function knowledgeLevelToUIMode(level: KnowledgeLevel): UIMode {
  return level;
}
